import { ChildModel, ModelBase } from 'store/model-base';
import { Util } from 'services/util';
import { Usage } from './usage';

export class LineUsage extends ModelBase {
    public static storeName: string = 'usage';

    public cycleStartDate: string;
    public cycleEndDate: string;
    public statementLineId: Id;
    public globalTravelPassUsage: Usage;
    public canMexTravelPassUsage: Usage;

    // these properties are private because model is shared by two api
    // in order to avoid bugs
    // if you need some info from those properties then make getter
    // but please not make it public :P
    private domesticUsage: Usage;
    private internationalUsage: Usage;
    private roamingUsage: Usage;
    private mexCanUsage: Usage;

    protected static get hasOne(): ChildModel[] {
        return [
            {
                attrName: 'domesticUsage',
                model: Usage
            },
            {
                attrName: 'internationalUsage',
                model: Usage
            },
            {
                attrName: 'roamingUsage',
                model: Usage
            },
            {
                attrName: 'mexCanUsage',
                model: Usage
            },
            {
                attrName: 'canMexTravelPassUsage',
                model: Usage
            },
            {
                attrName: 'globalTravelPassUsage',
                model: Usage
            }
        ];
    }

    // Usage of Canada and Mexico Global travel pass (Voice, text and data)
    public get usageOfCanMexGlobalTravelPass(): UsageLog[] {
        return [...this.canMexTravelPassUsage.voiceLog, ...this.canMexTravelPassUsage.textLog, ...this.canMexTravelPassUsage.dataLog];
    }

    // Usage of Global travel pass (Voice, text and data)
    public get usageOfGlobalTravelPass(): UsageLog[] {
        return [...this.globalTravelPassUsage.voiceLog, ...this.globalTravelPassUsage.textLog, ...this.globalTravelPassUsage.dataLog];
    }

    // Voice Logs
    public get calls(): UsageLog[] {
        return this.domesticUsage.voiceLog;
    }

    public get callsToNonUS(): UsageLog[] {
        return this.internationalUsage.voiceLog;
    }

    public get callsToMexCan(): UsageLog[] {
        return this.mexCanUsage.voiceLog;
    }

    public get callsWhileOutsideUS(): UsageLog[] {
        return this.roamingUsage.voiceLog;
    }

    // Message Logs
    public get messages(): UsageLog[] {
        return this.domesticUsage.textLog;
    }

    public get messagesToNonUS(): UsageLog[] {
        return this.internationalUsage.textLog;
    }

    public get messagesWhileOutsideUS(): UsageLog[] {
        return this.roamingUsage.textLog;
    }

    // Roaming Data
    public get dataWhileOutsideUS(): UsageLog[] {
        return this.roamingUsage.dataLog;
    }

    // Data Consumed getters
    public get domesticDataConsumed(): number {
        return parseFloat(Util.mbToGb(this.domesticUsage.dataConsumed));
    }

    // Domestic Data Cost getters
    public get domesticDataCost(): number {
        return this.domesticUsage.dataCost;
    }

    public get roamingDataConsumed(): number {
        return this.roamingUsage.dataConsumed || this.roamingUsage.dataLog
            .reduce((sum: number, dataLog: UsageLog) => sum + dataLog.dataUsed, 0);
    }

    // total data consumed in MB
    public get totalDataConsumed(): number {
        return this.domesticUsage.dataConsumed + this.roamingDataConsumed;
    }

    // Mex Can pass getters
    public get mexCanCalls(): number {
        return this.mexCanUsage.voiceCounts || this.mexCanUsage.voiceLog.length;
    }

    // voice counts
    public get domesticVoiceCounts(): number {
        return this.domesticUsage.voiceCounts || this.domesticUsage.voiceLog.length;
    }

    public get internationalVoiceCounts(): number {
        return this.internationalUsage.voiceCounts || this.internationalUsage.voiceLog.length;
    }

    public get roamingVoiceCounts(): number {
        return this.roamingUsage.voiceCounts || this.roamingUsage.voiceLog.length;
    }

    // voice duration
    public get internationalVoiceDuration(): number {
        return this.internationalUsage.voiceLog
            .reduce((sum: number, voiceLog: UsageLog) => sum + voiceLog.callDuration, 0);
    }

    public get mexCanVoiceDuration(): number {
        return this.mexCanUsage.voiceLog
            .reduce((sum: number, voiceLog: UsageLog) => sum + voiceLog.callDuration, 0);
    }

    public get roamingVoiceDuration(): number {
        return this.roamingUsage.voiceLog
            .reduce((sum: number, voiceLog: UsageLog) => sum + voiceLog.callDuration, 0);
    }

    // voice cost
    public get internationalVoiceCost(): number {
        return this.internationalUsage.voiceCost || this.internationalUsage.voiceLog
            .reduce((sum: number, voiceLog: UsageLog) => sum + voiceLog.cost, 0);
    }

    public get roamingVoiceCost(): number {
        return this.roamingUsage.voiceCost || this.roamingUsage.voiceLog
            .reduce((sum: number, voiceLog: UsageLog) => sum + voiceLog.cost, 0);
    }

    public get mexCanCallCost(): number {
        return this.mexCanUsage.voiceCost || this.mexCanUsage.voiceLog
            .reduce((sum: number, voiceLog: UsageLog) => sum + voiceLog.cost, 0);
    }

    // messages counts
    public get domesticMessageCounts(): number {
        return this.domesticUsage.textCounts || this.domesticUsage.textLog.length;
    }

    public get internationalMessageCounts(): number {
        return this.internationalUsage.textCounts || this.internationalUsage.textLog.length;
    }

    public get roamingMessageCounts(): number {
        return this.roamingUsage.textCounts || this.roamingUsage.textLog.length;
    }

    // message cost
    public get internationalMessageCost(): number {
        return this.internationalUsage.textCost || this.internationalUsage.textLog
            .reduce((sum: number, textLog: UsageLog) => sum + textLog.cost, 0);
    }

    public get roamingMessageCost(): number {
        return this.roamingUsage.textCost || this.roamingUsage.textLog
            .reduce((sum: number, textLog: UsageLog) => sum + textLog.cost, 0);
    }

    // Roaming data cost
    public get roamingDataCost(): number {
        return this.roamingUsage.dataCost || this.roamingUsage.dataLog
            .reduce((sum: number, dataLog: UsageLog) => sum + dataLog.cost, 0);
    }
}
